import { Building, PhotoModel } from '../../types/protocols';
import { BuildingCheck } from './buildingChecksUtils';

/** The image a location-only check's marker was placed on, and where on it. */
export interface CheckLocationMarker {
    /** Level name, "Takplan" or "Satellitbild". */
    label: string;
    /** Null when the image is gone from the building: shown as "Bild saknas", never moved to another image. */
    storagePath: string | null;
    /** Fractions of the image (0..1); NaN when the marker has no reference size. */
    x: number;
    y: number;
}

const marker = (location: NonNullable<BuildingCheck['location']>, label: string, image: PhotoModel | null): CheckLocationMarker => {
    const { x, y, width, height } = location.marker;
    const hasFrame = width > 0 && height > 0;
    return {
        label,
        storagePath: image?.storagePath || null,
        x: hasFrame ? x / width : NaN,
        y: hasFrame ? y / height : NaN,
    };
};

/**
 * The marker of a check linked to a place instead of a component. `sourceKey` is
 * the picker's option key (`LocationMarkerPicker`): `satellite_<imageKey>` or
 * `blueprint_<levelKey|roofPlan>_<imageKey>`. Keys may contain `_`, so it is
 * matched against the building's images rather than split. The image is looked
 * up among all of the owner's drawings, so a marker stays on the drawing it was
 * placed on after a newer one is uploaded. Null when the check has no location.
 */
export const checkLocationMarker = (
    location: BuildingCheck['location'],
    building: Building | null | undefined
): CheckLocationMarker | null => {
    if (!location?.marker) return null;
    const model = building?.model;

    if (location.type === 'satellite') {
        const images = model?.mapImages || {};
        const imageKey = Object.keys(images).find((key) => location.sourceKey === `satellite_${key}`);
        return marker(location, 'Satellitbild', imageKey ? images[imageKey] : null);
    }

    const structure = model?.structure;
    const owners: { ownerKey: string; label: string; blueprints?: { [key: string]: PhotoModel } }[] = [
        ...Object.entries(structure?.levels || {}).map(([levelKey, level]) => ({
            ownerKey: levelKey,
            label: level.name || 'Ritning',
            blueprints: level.blueprints,
        })),
        { ownerKey: 'roofPlan', label: 'Takplan', blueprints: structure?.roofPlan?.blueprints },
    ];
    for (const owner of owners) {
        if (!location.sourceKey.startsWith(`blueprint_${owner.ownerKey}_`)) continue;
        const imageKey = Object.keys(owner.blueprints || {}).find(
            (key) => location.sourceKey === `blueprint_${owner.ownerKey}_${key}`
        );
        if (imageKey) return marker(location, owner.label, owner.blueprints![imageKey]);
    }
    // The owner's prefix alone names the level even when the drawing is gone.
    const owner = owners.find((o) => location.sourceKey.startsWith(`blueprint_${o.ownerKey}_`));
    return marker(location, owner?.label ?? 'Ritning', null);
};
